import {
  getTenantIdByHost,
  ZOOCASA_TENANT_ID,
} from 'tenants';

declare global {
  interface Window {
    mapkit: typeof mapkit & {
      load(libraries: string | string[]): void;
      loadedLibraries: string[];
      __initialized?: boolean;
    };
  }
}

let mapKitCorePromise: Promise<void> | null = null;

export async function loadMapKitCore() {
  if (typeof window === 'undefined') return;

  if (mapKitCorePromise) {
    return mapKitCorePromise;
  }

  mapKitCorePromise = (async () => {
    if (!window.mapkit) {
      // This promise resolves when the browser finishes downloading and evaluating MapKit JS.
      await new Promise(resolve => {
        const element = document.createElement('script');
        element.addEventListener('load', resolve, { once : true });
        element.src = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.core.js';
        element.crossOrigin = 'async';
        document.head.appendChild(element);
      });

      await new Promise<void>(resolve => {
        const checkCondition = () => {
          if (window?.mapkit) {
            resolve();
          } else {
            setTimeout(checkCondition, 100);
          }
        };

        checkCondition();
      });
    }

    if (!window.mapkit.__initialized) {
      await new Promise<void>((resolve, reject) => {
        mapkit.init({
          authorizationCallback: function (done) {
            if (process.env.NEXT_PUBLIC_APPLE_MAPKIT_JWT_VERCEL) {
              done((process.env.NEXT_PUBLIC_APPLE_MAPKIT_JWT_VERCEL as string));
              return;
            }
            const tenant = getTenantIdByHost(location.hostname) ?? ZOOCASA_TENANT_ID;
            fetch(`/api/gettoken?tenant=${tenant}`)
              .then(r => r.text())
              .then(done);
          },
        });
        mapkit.addEventListener('configuration-change', function(event) {
          switch (event.status) {
            case 'Initialized':
              console.debug('MapKit JS initialized and configured.');
              resolve();
              break;
            case 'Refreshed':
              console.debug('The MapKit JS configuration was updated.');
              resolve();
              break;
          }
        });

        mapkit.addEventListener('error', function(event) {
          switch (event.status) {
            case 'Unauthorized':
              console.error('The MapKit JS Initialized Was Unauthorized.');
              reject(event.status);
              break;
            case 'Too Many Requests':
              console.error('Exceeded number of requests allowed.');
              reject(event.status);
              break;
          }
        });
      });

      await new Promise<void>(resolve => {
        window?.mapkit.load('full-map');
        window?.mapkit.addEventListener('load' as any, function() {
          resolve();
        });
      });
      window.mapkit.__initialized = true;
    }
  })();

  return mapKitCorePromise;
}